import { useTheme } from '@emotion/react';
import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback } from 'react';

import { Flex, Name, Table, Text } from '~/components/blocks';
import { DeliveryMethodField } from '~/components/partials/DeliveryMethodField';
import { DraftAppointmentStatusLabel } from '~/components/partials/DraftAppointmentStatusLabel';
import { MedicationInstructionMethodLabel } from '~/components/partials/MedicationInstructionMethodLabel';
import { ReceptionMemos } from '~/components/partials/ReceptionMemos';
import { ReceptionDraftAppointmentFragment } from '~/graphql/generated/graphql';
import { useUberOrganizationSetting } from '~/hooks/use-uber_organization_setting';
import { profileDrawerState } from '~/state/partials/patient_profile_drawer/atoms';
import {
  checkinEntryProfileDrawerState,
  draftAppointmentPatientProfileDrawerState,
  lineVisitorProfileDrawerState,
  outpatientQuestionnaireEntryProfileDrawerState,
  webVisitorProfileDrawerState,
} from '~/state/reception/atoms';
import { Label } from '~/utils/label';

import { TableRow } from './TableRow';

type Props = {
  draftAppointment: ReceptionDraftAppointmentFragment;
  updated: boolean;
  activeMedixs: boolean;
};

export const DraftAppointment = React.memo((props: Props) => {
  const theme = useTheme();
  const { draftAppointment } = props;
  const patient = draftAppointment.draftAppointmentPatient;
  const { enable: enabledUber } = useUberOrganizationSetting();
  const setDraftAppointmentPatientProfileDrawerState = useSetAtom(
    draftAppointmentPatientProfileDrawerState,
  );
  const resetCheckinEntryProfileDrawerState = useResetAtom(checkinEntryProfileDrawerState);
  const resetProfileDrawerState = useResetAtom(profileDrawerState);
  const resetLineVisitorProfileDrawerState = useResetAtom(lineVisitorProfileDrawerState);
  const resetWebVisitorProfileDrawerState = useResetAtom(webVisitorProfileDrawerState);
  const resetOutpatientQuestionnaireEntryProfileDrawerState = useResetAtom(
    outpatientQuestionnaireEntryProfileDrawerState,
  );

  const handleClick = useCallback(() => {
    setDraftAppointmentPatientProfileDrawerState({
      isOpen: true,
      patientId: patient.id,
      draftAppointmentId: draftAppointment.id,
      error: null,
    });
    resetCheckinEntryProfileDrawerState();
    resetProfileDrawerState();
    resetLineVisitorProfileDrawerState();
    resetWebVisitorProfileDrawerState();
    resetOutpatientQuestionnaireEntryProfileDrawerState();
  }, [
    draftAppointment.id,
    patient.id,
    setDraftAppointmentPatientProfileDrawerState,
    resetCheckinEntryProfileDrawerState,
    resetProfileDrawerState,
    resetLineVisitorProfileDrawerState,
    resetWebVisitorProfileDrawerState,
    resetOutpatientQuestionnaireEntryProfileDrawerState,
  ]);

  return (
    <TableRow updated={props.updated} onClick={handleClick}>
      <Table.Td nowrap>
        <Flex alignItems="center">
          <Text size="m" fontWeight="bold" paddingLeft={theme.space.m}>
            未確定
          </Text>
        </Flex>
      </Table.Td>
      <Table.Td nowrap>
        <DraftAppointmentStatusLabel status={props.draftAppointment.draftAppointmentStatus} />
      </Table.Td>
      {props.activeMedixs && (
        <Table.Td nowrap>
          <Text color="navy" size="s">
            未連携
          </Text>
        </Table.Td>
      )}
      <Table.Td nowrap>
        <Name
          familyName={patient.familyName}
          givenName={patient.givenName}
          phoneticFamilyName={patient.phoneticFamilyName}
          phoneticGivenName={patient.phoneticGivenName}
        />
      </Table.Td>
      <Table.Td nowrap>
        <Text size="m" whiteSpace="nowrap">
          {Label.age(patient.birthDate)}
        </Text>
        <Text size="s" whiteSpace="nowrap">
          （{Label.sex(patient.sex)}）
        </Text>
      </Table.Td>
      <Table.Td>
        <Flex alignItems="center">
          <MedicationInstructionMethodLabel
            method={draftAppointment.telemedicine ? 'telemedicine' : 'faceToFace'}
            use="short"
            size="s"
          />
        </Flex>
      </Table.Td>
      <Table.Td>
        <DeliveryMethodField
          deliveryMethod={draftAppointment.draftAppointmentDeliveryMethod}
          size="m"
        />
      </Table.Td>
      {enabledUber && <Table.Td>-</Table.Td>}
      <Table.Td>-</Table.Td>
      <Table.Td>
        <ReceptionMemos memo={draftAppointment.description} />
      </Table.Td>
    </TableRow>
  );
});

DraftAppointment.displayName = 'DraftAppointment';
